import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ServiceContext } from "context/ServiceContextProvider";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Badge,
  Input,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { PriceViewCompact } from "../CommonComponents/PriceViewComponent";
import { useGetBulkApprovalEligiblePosQuery } from "slices/workflow/WorkFlowSlice";
import type { EligibleRow } from "./types";

const POBulkApprovalList = () => {
  document.title = "PO Bulk Approval | CompliPro";
  const navigate = useNavigate();
  const context = useContext(ServiceContext);
  const selectedRoleName = context?.selectedRoleName;

  const [page, setPage] = useState({ page: 1, pageSize: 30, search: "" });
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data, isFetching, refetch } = useGetBulkApprovalEligiblePosQuery(page);

  const rows: EligibleRow[] = data?.results || [];
  const actionableCount = rows.filter((r) => r.actionable).length;
  const roleCannotApprove = !isFetching && rows.length > 0 && actionableCount === 0;

  // Category lock: once a row is selected, only its category stays selectable.
  const lockedCategory = useMemo(() => {
    if (!selectedIds.length) return null;
    const first = rows.find((r) => selectedIds.includes(r.id));
    return first ? (first.category_name || "").toUpperCase() : null;
  }, [selectedIds, rows]);

  const isRowSelectable = (row: EligibleRow) => {
    if (!row.actionable) return false;
    if (
      lockedCategory &&
      (row.category_name || "").toUpperCase() !== lockedCategory
    )
      return false;
    return true;
  };

  const selectableRows = rows.filter(isRowSelectable);
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((r) => selectedIds.includes(r.id));

  const toggleRow = (row: EligibleRow, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, row.id] : prev.filter((id) => id !== row.id)
    );
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const firstCategory = (
        selectableRows[0]?.category_name || ""
      ).toUpperCase();
      const sameCategory = rows.filter(
        (r) =>
          r.actionable &&
          (r.category_name || "").toUpperCase() === firstCategory
      );
      setSelectedIds(sameCategory.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleProceed = () => {
    const selectedRows = rows.filter((r) => selectedIds.includes(r.id));
    if (!selectedRows.length) return;
    navigate("/po-bulk-approval/review", { state: { rows: selectedRows } });
  };

  const statusBadge = (row: EligibleRow) => {
    if (row.actionable) {
      return (
        <div>
          <Badge color="success">
            <i className="ri-user-follow-line align-middle me-1"></i>
            Awaiting my approval
          </Badge>
          {row.my_step &&
            row.my_step.level !== row.current_step?.level && (
              <div className="text-muted fs-11 mt-1">
                My step: {row.my_step.step_name}
              </div>
            )}
        </div>
      );
    }
    return (
      <Badge
        color="light"
        className="text-muted border"
        title={row.blocked_reason || undefined}
      >
        <i className="ri-time-line align-middle me-1"></i>
        {row.blocked_reason || "Not actionable"}
      </Badge>
    );
  };

  const outcomeBadge = (row: EligibleRow) => {
    if (!row.actionable) {
      return <span className="text-muted">—</span>;
    }
    const skipNote = row.skipped_levels?.length
      ? ` (skips ${row.skipped_levels
          .map((s) => s.step_name || `Level ${s.level}`)
          .join(", ")})`
      : "";
    if (row.outcome?.type === "fully_approved") {
      return (
        <span className="badge bg-success-subtle text-success border border-success-subtle">
          <i className="ri-check-double-line align-middle me-1"></i>
          Will be fully approved{skipNote}
        </span>
      );
    }
    return (
      <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
        <i className="ri-arrow-right-line align-middle me-1"></i>
        Will move to Level {row.outcome?.next_level}
        {row.outcome?.next_roles?.length
          ? ` (${row.outcome.next_roles.join(", ")})`
          : ""}
        {skipNote}
      </span>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="PO Bulk Approval" pageTitle="Resource Inventory" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <div>
                      <h5 className="mb-1">Pending Purchase Order approvals</h5>
                      <p className="text-muted mb-0 fs-13">
                        Rows marked <strong>Awaiting my approval</strong> are at
                        your active role's step and can be approved in bulk
                        (same category only). Others show which role they are
                        waiting on.
                      </p>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <Input
                        type="text"
                        placeholder="Search PO number..."
                        style={{ minWidth: 200 }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setSelectedIds([]);
                            setPage((p) => ({ ...p, page: 1, search: searchText }));
                          }
                        }}
                      />
                      <Button
                        color="light"
                        onClick={() => {
                          setSelectedIds([]);
                          setPage((p) => ({ ...p, page: 1, search: searchText }));
                        }}
                      >
                        <i className="ri-search-line align-middle"></i>
                      </Button>
                      <Button
                        color="light"
                        onClick={() => {
                          setSelectedIds([]);
                          refetch();
                        }}
                      >
                        <i className="ri-refresh-line align-middle"></i>
                      </Button>
                      <Button
                        color="primary"
                        disabled={selectedIds.length === 0}
                        onClick={handleProceed}
                      >
                        <i className="ri-arrow-right-circle-line align-middle me-1"></i>
                        Proceed to Approval ({selectedIds.length})
                      </Button>
                    </div>
                  </div>

                  {roleCannotApprove && (
                    <div className="alert alert-warning py-2">
                      <i className="ri-alert-line me-1"></i>
                      <strong>
                        Your role{selectedRoleName ? ` (${selectedRoleName})` : ""} is
                        not able to approve these purchase orders.
                      </strong>{" "}
                      Each PO below shows which role it is waiting on. Switch to
                      an approver role to use bulk approval.
                    </div>
                  )}

                  {lockedCategory && (
                    <div className="alert alert-info py-2">
                      <i className="ri-information-line me-1"></i>
                      Category locked to <strong>{lockedCategory}</strong> — POs
                      of other categories are disabled. Clear the selection to
                      switch.
                    </div>
                  )}

                  {isFetching ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : rows.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="ri-checkbox-circle-line display-4 text-muted opacity-50"></i>
                      <h5 className="mt-3 text-muted">
                        No pending purchase order approvals
                      </h5>
                      <p className="text-muted mb-0 fs-13">
                        POs on your projects with a pending approval will appear
                        here.
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 40 }}>
                              <Input
                                type="checkbox"
                                checked={allSelected}
                                disabled={actionableCount === 0}
                                onChange={(e) => toggleAll(e.target.checked)}
                              />
                            </th>
                            <th>PO Number</th>
                            <th>Project</th>
                            <th>Supplier</th>
                            <th>Category</th>
                            <th>Total Cost</th>
                            <th>Current Level</th>
                            <th>Status</th>
                            <th>If I approve</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => {
                            const selectable = isRowSelectable(row);
                            const checked = selectedIds.includes(row.id);
                            return (
                              <tr
                                key={row.id}
                                className={!row.actionable ? "opacity-75" : ""}
                              >
                                <td>
                                  <Input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={!selectable}
                                    title={
                                      row.blocked_reason ||
                                      (!selectable && lockedCategory
                                        ? "Different category from your selection"
                                        : undefined)
                                    }
                                    onChange={(e) =>
                                      toggleRow(row, e.target.checked)
                                    }
                                  />
                                </td>
                                <td>
                                  <Link
                                    to={`/purchase-order-detail/${row.po_number}/`}
                                    className="fw-medium"
                                  >
                                    {row.po_number}
                                  </Link>
                                </td>
                                <td>{row.project_name || "-"}</td>
                                <td>{row.supplier_name || "-"}</td>
                                <td>
                                  <Badge color="primary" className="badge-border">
                                    {row.category_name || "-"}
                                  </Badge>
                                </td>
                                <td>
                                  {row.total_cost != null ? (
                                    <PriceViewCompact
                                      price={Number(row.total_cost).toFixed(2)}
                                    />
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>
                                  {row.current_step?.step_name || "-"}
                                  {row.current_step?.roles?.length ? (
                                    <div className="text-muted fs-11">
                                      {row.current_step.roles.join(", ")}
                                    </div>
                                  ) : null}
                                </td>
                                <td>{statusBadge(row)}</td>
                                <td>{outcomeBadge(row)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {/* Server pagination */}
                  {data && data.count > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <span className="text-muted fs-13">
                        Page {data.current_page} of {data.total_page} •{" "}
                        {data.count} record{data.count !== 1 ? "s" : ""}
                      </span>
                      <div className="d-flex gap-2">
                        <Button
                          color="light"
                          size="sm"
                          disabled={!data.previous}
                          onClick={() => {
                            setSelectedIds([]);
                            setPage((p) => ({ ...p, page: p.page - 1 }));
                          }}
                        >
                          Previous
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          disabled={!data.next}
                          onClick={() => {
                            setSelectedIds([]);
                            setPage((p) => ({ ...p, page: p.page + 1 }));
                          }}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default POBulkApprovalList;
